import { apiRequest } from "../../shared/api/client";
import {
  SHIFT_PRODUCTION_FORM_API_BASE,
  corporateUserOptionListResponseSchema,
  approvalAssignmentListResponseSchema,
  saveApprovalAssignmentResponseSchema,
  deleteApprovalAssignmentResponseSchema,
  approvalMailStatusListResponseSchema,
  lineConfigurationListResponseSchema,
  lineConfigurationResponseSchema,
  shiftFormDocumentSettingsResponseSchema,
  shiftFormManagerDashboardResponseSchema,
  linesResponseSchema,
  shiftFormLineUnitsResponseSchema,
  operatorAdminListResponseSchema,
  operatorAdminResponseSchema,
  operatorLoginResponseSchema,
  approverLoginResponseSchema,
  approverPinIssuedResponseSchema,
  operatorDraftResponseSchema,
  operatorPinIssuedResponseSchema,
  operatorPinRevealResponseSchema,
  shiftFormSessionResponseSchema,
  shiftProductionFormListResponseSchema,
  shiftProductionFormResponseSchema,
  type ShiftProductionFormPayload,
  type ShiftFormDocumentSettings,
  type ApprovalStage,
  type DeleteApprovalAssignmentRequest,
  type SaveApprovalAssignmentRequest,
  type ShiftFormManagerDashboardFilters,
  type ShiftProductionLineName,
} from "../../../shared/shift-production-form/contracts";

type FormListView = "mine" | "pending" | "processed" | "accessible" | "completed" | "unassigned";
export type FormListFilters = {
  stage?: ApprovalStage;
  approvedToday?: boolean;
  departmentName?: string;
  lineName?: string;
  productionDate?: string;
  dateFrom?: string;
  dateTo?: string;
  shift?: string;
  operatorName?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
};

function parse<T>(schema: { parse: (value: unknown) => T }, value: unknown): T {
  return schema.parse(value);
}

async function parsedRequest<T>(
  schema: { parse: (value: unknown) => T },
  path: string,
  options?: RequestInit,
) {
  return parse(schema, await apiRequest<unknown>(path, options));
}

export function getShiftFormSession() {
  return parsedRequest(
    shiftFormSessionResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/session`,
    { cache: "no-store" },
  );
}

export function getDocumentSettings() {
  return parsedRequest(
    shiftFormDocumentSettingsResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/document-settings`,
    { cache: "no-store" },
  );
}

export function saveDocumentSettings(
  settings: Pick<
    ShiftFormDocumentSettings,
    "documentNo" | "publicationDate" | "revisionDate" | "revisionNo"
  >,
  expectedVersion: number,
) {
  return parsedRequest(
    shiftFormDocumentSettingsResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/document-settings`,
    {
      method: "PUT",
      body: JSON.stringify({ ...settings, expectedVersion }),
    },
  );
}

export function loginShiftFormOperator(pin: string) {
  return parsedRequest(
    operatorLoginResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/operator/session`,
    { method: "POST", body: JSON.stringify({ pin }) },
  );
}

export function loginShiftFormApprover(pin: string) {
  return parsedRequest(
    approverLoginResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/approver/session`,
    { method: "POST", body: JSON.stringify({ pin }) },
  );
}

export function recordShiftFormApproverActivity() {
  return parsedRequest(
    shiftFormSessionResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/approver/session/activity`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function logoutShiftFormApprover() {
  return apiRequest<void>(
    `${SHIFT_PRODUCTION_FORM_API_BASE}/approver/session`,
    { method: "DELETE" },
  );
}

export function recordShiftFormHumanActivity() {
  return parsedRequest(
    shiftFormSessionResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/operator/session/activity`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function logoutShiftFormOperator() {
  return apiRequest<void>(
    `${SHIFT_PRODUCTION_FORM_API_BASE}/operator/session`,
    { method: "DELETE" },
  );
}

export function getOperatorRecentSubmissions(offset = 0, signal?: AbortSignal) {
  return parsedRequest(
    shiftProductionFormListResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/operator/recent-submissions?offset=${offset}`,
    { cache: "no-store", signal },
  );
}

export function reviseOperatorForm(formId: string, expectedVersion: number) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/operator/forms/${encodeURIComponent(formId)}/revise`,
    { method: "POST", body: JSON.stringify({ expectedVersion }) },
  );
}

export function getOperatorDraft(signal?: AbortSignal) {
  return parsedRequest(
    operatorDraftResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/operator/draft`,
    { cache: "no-store", signal },
  );
}

export function getShiftFormLines() {
  return parsedRequest(
    linesResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/lines`,
  );
}

export function getShiftFormLineUnits() {
  return parsedRequest(
    shiftFormLineUnitsResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/line-units`,
    { cache: "no-store" },
  );
}

export function getShiftFormList(
  view: FormListView,
  filters: FormListFilters = {},
) {
  const query = new URLSearchParams({ view });
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") query.set(key, String(value));
  });
  return parsedRequest(
    shiftProductionFormListResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/forms?${query.toString()}`,
    { cache: "no-store" },
  );
}

export function getShiftFormManagerDashboard(
  filters: ShiftFormManagerDashboardFilters = {},
  testMode = false,
) {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") query.set(key, String(value));
  });
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return parsedRequest(
    shiftFormManagerDashboardResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/manager/${testMode ? "test-dashboard" : "dashboard"}${suffix}`,
    { cache: "no-store" },
  );
}

export function getShiftForm(formId: string, testMode = false) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/${testMode ? "manager/test-forms" : "forms"}/${encodeURIComponent(formId)}`,
    { cache: "no-store" },
  );
}

export function saveNewShiftForm(
  payload: ShiftProductionFormPayload,
  idempotencyKey: string,
) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/forms`,
    { method: "POST", body: JSON.stringify({ payload, idempotencyKey }) },
  );
}

export function saveShiftForm(
  formId: string,
  expectedVersion: number,
  payload: ShiftProductionFormPayload,
) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/forms/${encodeURIComponent(formId)}`,
    { method: "PUT", body: JSON.stringify({ expectedVersion, payload }) },
  );
}

export function submitShiftForm(formId: string, expectedVersion: number) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/forms/${encodeURIComponent(formId)}/submit`,
    { method: "POST", body: JSON.stringify({ expectedVersion }) },
  );
}

export function decideShiftForm(
  formId: string,
  expectedVersion: number,
  revisionNumber: number,
  action: "APPROVE",
  explanation: string,
  missingMeasurementAcceptanceReason: string,
) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/forms/${encodeURIComponent(formId)}/decisions`,
    {
      method: "POST",
      body: JSON.stringify({
        expectedVersion,
        revisionNumber,
        action,
        explanation: explanation || null,
        missingMeasurementAcceptanceReason:
          missingMeasurementAcceptanceReason || null,
      }),
    },
  );
}

export function getOperators() {
  return parsedRequest(
    operatorAdminListResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/operators`,
    { cache: "no-store" },
  );
}

export function createOperator(name: string) {
  return parsedRequest(
    operatorPinIssuedResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/operators`,
    { method: "POST", body: JSON.stringify({ name }) },
  );
}

export function resetOperatorPin(operatorId: string, expectedUpdatedAt: string) {
  return parsedRequest(
    operatorPinIssuedResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/operators/${encodeURIComponent(operatorId)}/reset-pin`,
    { method: "POST", body: JSON.stringify({ expectedUpdatedAt }) },
  );
}

export function setOperatorActive(operatorId: string, active: boolean) {
  return parsedRequest(
    operatorAdminResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/operators/${encodeURIComponent(operatorId)}`,
    { method: "PATCH", body: JSON.stringify({ active }) },
  );
}

export function getCorporateUsers() {
  return parsedRequest(
    corporateUserOptionListResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/corporate-users`,
    { cache: "no-store" },
  );
}

export function issueApproverPin(userId: string, expectedUpdatedAt: string | null) {
  return parsedRequest(
    approverPinIssuedResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/corporate-users/${encodeURIComponent(userId)}/approver-pin`,
    { method: "POST", body: JSON.stringify({ expectedUpdatedAt }) },
  );
}

export function getApprovalAssignments() {
  return parsedRequest(
    approvalAssignmentListResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/approval-assignments`,
    { cache: "no-store" },
  );
}

export function saveApprovalAssignment(input: SaveApprovalAssignmentRequest) {
  return parsedRequest(
    saveApprovalAssignmentResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/approval-assignments`,
    { method: "PUT", body: JSON.stringify(input) },
  );
}

export function removeApprovalAssignment(input: DeleteApprovalAssignmentRequest) {
  return parsedRequest(
    deleteApprovalAssignmentResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/approval-assignments`,
    { method: "DELETE", body: JSON.stringify(input) },
  );
}

export function getApprovalMailStatuses() {
  return parsedRequest(
    approvalMailStatusListResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/approval-mail-status`,
    { cache: "no-store" },
  );
}

export function getLineConfigurations() {
  return parsedRequest(
    lineConfigurationListResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/line-configurations`,
    { cache: "no-store" },
  );
}

type LineConfiguration = Awaited<
  ReturnType<typeof getLineConfigurations>
>["items"][number];
export function saveLineConfiguration(
  lineName: ShiftProductionLineName,
  configuration: Omit<LineConfiguration, "lineName" | "version">,
  expectedVersion: number,
) {
  return parsedRequest(
    lineConfigurationResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/line-configurations/${encodeURIComponent(lineName)}`,
    {
      method: "PUT",
      body: JSON.stringify({ ...configuration, expectedVersion }),
    },
  );
}

export function adminCreateForm(
  operatorId: string,
  reason: string,
  payload: ShiftProductionFormPayload,
  idempotencyKey: string,
) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/forms`,
    {
      method: "POST",
      body: JSON.stringify({ operatorId, reason, payload, idempotencyKey }),
    },
  );
}

export function transferForm(
  formId: string,
  expectedVersion: number,
  newOperatorId: string,
  reason: string,
) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/forms/${encodeURIComponent(formId)}/transfer`,
    {
      method: "POST",
      body: JSON.stringify({ expectedVersion, newOperatorId, reason }),
    },
  );
}

export function grantLateCompletion(
  formId: string,
  expectedVersion: number,
  reason: string,
  expiresAt: string,
) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/forms/${encodeURIComponent(formId)}/late-grant`,
    {
      method: "POST",
      body: JSON.stringify({ expectedVersion, reason, expiresAt }),
    },
  );
}

export function reopenCompletedForm(
  formId: string,
  expectedVersion: number,
  reason: string,
) {
  return parsedRequest(
    shiftProductionFormResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/forms/${encodeURIComponent(formId)}/reopen`,
    { method: "POST", body: JSON.stringify({ expectedVersion, reason }) },
  );
}

export function revealOperatorPin(operatorId: string) {
  return parsedRequest(
    operatorPinRevealResponseSchema,
    `${SHIFT_PRODUCTION_FORM_API_BASE}/admin/operators/${encodeURIComponent(operatorId)}/reveal-pin`,
    { method: "POST", cache: "no-store", body: JSON.stringify({}) },
  );
}
